import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Container, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import { useAuth } from 'services/auth/authContext';
import { getCashbackWalletByEmail } from 'services/firebase/growlersCashbackService';
import { formatCurrencyBRL } from 'utils/currency';

import { styles } from './styles';

const maskCPF = (cpf?: string) => {
  if (!cpf || cpf.length < 3) return '***********';
  const visible = cpf.slice(-3);
  return `********${visible}`;
};

const MyAccount: React.FC = () => {
  const { personalData } = useSubscription();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [cashbackBalance, setCashbackBalance] = useState<number | null>(null);
  const [cashbackLoading, setCashbackLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadCashback = async () => {
      if (!currentUser || currentUser.isAnonymous || !currentUser.email) {
        setCashbackBalance(null);
        return;
      }

      setCashbackLoading(true);
      try {
        const wallet = await getCashbackWalletByEmail(currentUser.email);
        if (!cancelled) {
          setCashbackBalance(wallet?.balance ?? 0);
        }
      } finally {
        if (!cancelled) {
          setCashbackLoading(false);
        }
      }
    };

    void loadCashback();

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  return (
    <Container maxWidth="sm" sx={styles.container}>
      <Typography variant="h5" sx={styles.title} gutterBottom>
        Minha Conta
      </Typography>

      <Box sx={styles.section}>
        <Box sx={styles.sectionContent}>
          <Typography sx={styles.label}>Cashback (growlers)</Typography>
          {cashbackLoading ? (
            <CircularProgress size={20} sx={{ mt: 1 }} />
          ) : (
            <Typography sx={styles.value}>
              R${' '}
              {formatCurrencyBRL(
                typeof cashbackBalance === 'number' ? cashbackBalance : 0,
              )}
            </Typography>
          )}
          <Typography sx={styles.description}>
            Use o saldo nas próximas compras de /growlers, com a mesma conta
            logada.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/growlers')}>
          Usar
        </Button>
      </Box>

      <Box sx={styles.section}>
        <Box sx={styles.sectionContent}>
          <Typography sx={styles.label}>Nome</Typography>
          <Typography sx={styles.value}>{personalData?.fullName}</Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/edit-name')}>
          Editar
        </Button>
      </Box>

      <Box sx={styles.section}>
        <Box sx={styles.sectionContent}>
          <Typography sx={styles.label}>Email</Typography>
          <Typography sx={styles.value}>{personalData?.email}</Typography>
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Box sx={styles.sectionContent}>
          <Typography sx={styles.label}>CPF</Typography>
          <Typography sx={styles.value}>
            {maskCPF(personalData?.cpf)}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Box sx={styles.sectionContent}>
          <Typography sx={styles.label}>Telefone</Typography>
          <Typography sx={styles.value}>{personalData?.phoneNumber}</Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/edit-phone')}>
          Editar
        </Button>
      </Box>

      <Box sx={styles.section}>
        <Box sx={styles.sectionContent}>
          <Typography sx={styles.label}>Senha</Typography>
          <Typography sx={styles.value}>********</Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/edit-password')}>
          Editar
        </Button>
      </Box>
    </Container>
  );
};

export default MyAccount;
